import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Participation,
  useMarkAsNotInterested,
  Session,
  useSessionLinks,
} from '@app/api';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import classnames from 'classnames';
import { IconsMeetingList } from './IconsMeetingList';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@app/core';

const useStyles = createUseStyles((theme: Theme) => ({
  globalContainer: {
    width: '90%',
    minWidth: '90%',
    position: 'relative',
    border: '1px solid',
    borderColor: theme.colors.grayBorder,
    borderRadius: theme.borderRadius.std,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [theme.media.homeScreen]: {
      marginBottom: theme.marginBase * 2,
      width: '100%',
      minWidth: '100%',
      marginRight: 0,
    },
  },
  container: {
    width: '100%',
    height: `calc(100% - ${theme.height.regular}px)`,
    padding: theme.marginBase * 3,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  title: {
    ...theme.fonts.h5,
  },
  descriptionContainer: {
    overflow: 'hidden',
  },
  description: {
    ...theme.fonts.caption,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineClamp: 3,
    display: '-webkit-box',
    boxOrient: 'vertical',
  },
  descriptionHeight: {
    height: 'calc(100% - 25px)',
  },
  viewMore: {
    ...theme.fonts.label,
    paddingTop: theme.marginBase / 2,
    color: theme.colors.blueAccessible,
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    height: theme.height.regular,
  },
  buttonWrapper: {
    flex: 1,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: [theme.marginBase * 2, 'auto'],
    backgroundColor: theme.colors.blueMuted,
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
  },
  buttonWrapperRed: {
    backgroundColor: theme.colors.redMuted,
    color: theme.colors.redAccessible,
  },
  btnText: {
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
  },
  btnRed: {
    color: theme.colors.redAccessible,
  },
  iconContainer: {
    margin: 0,
  },
}));

interface Props {
  participation?: Participation;
  session: Session;
  className?: string;
}

export const ApplyToStudies = ({
  participation,
  session,
  className,
}: Props) => {
  const classes = useStyles();
  const toast = useToast();
  const { mutateAsync: markAsNotInterested, isLoading } =
    useMarkAsNotInterested(participation?.id);
  const { t } = useTranslation();
  const [isTruncated, setIsTruncated] = useState<boolean>(false);

  const ref = useRef<any>(null);
  const links = session.links;
  let linkId = links?.find((l) => l.recruitmentByTandemz && l.defaultLink)?.id;
  if (!linkId && links?.length) {
    linkId = links[0].id;
  }

  useEffect(() => {
    const clientHeight = ref.current?.clientHeight;
    const scrollHeight = ref.current?.scrollHeight;
    if (!scrollHeight || !clientHeight) {
      setIsTruncated(false);
    }
    setIsTruncated(clientHeight < scrollHeight);
  }, []);

  if (!session) return null;

  const link = participation ? `/p/${participation.id}` : `/s/${linkId}`;

  return (
    <div className={classnames(classes.globalContainer, className)}>
      <a className={classes.container} href={link}>
        <h1 className={classes.title}>{session.title}</h1>
        {session.description && (
          <div className={classes.descriptionContainer}>
            <h5
              className={classes.description}
              id={`${participation?.id}ViewMoreMatched`}
              ref={ref}
            >
              {session.description}
            </h5>
            {isTruncated && (
              <span className={classes.viewMore}>
                {t('home.applyStudies.viewMore')}
              </span>
            )}
          </div>
        )}
        <div>
          <IconsMeetingList
            className={classes.iconContainer}
            session={session}
            focus={false}
            participation={participation}
          />
        </div>
      </a>
      <div className={classes.buttonContainer}>
        <Link
          to={link}
          className={classes.buttonWrapper}
          state={{ allowBack: true }}
        >
          {t('home.applyStudies.btnApplyToStudies')}
        </Link>
        {participation && (
          <button
            className={classnames(
              classes.buttonWrapper,
              classes.buttonWrapperRed,
            )}
            onClick={async () => {
              try {
                await markAsNotInterested();
                toast.saved();
              } catch (e) {
                toast.exception(e);
              }
            }}
            disabled={isLoading}
          >
            {t('home.applyStudies.btnNotInterested')}
          </button>
        )}
      </div>
    </div>
  );
};
